import * as THREE from 'three';
import { boardRect, type BoardRect, type Viewport } from '../play/layout';
import { createPaperTexture, createShadowTexture } from '../fx/paperTexture';
import { alphaAt, type AlphaGrid } from '../fx/layerAlpha';
import { easeOutBack, easeOutCubic } from '../fx/tween';
import type { Circle } from '../content/manifest';

// 通关 → 立体书（spec §6）。世界单位 = CSS px，原点在画布中心，y 向上（与 BoardScene 相同），
// 这样在 fov≈0.5° 时 z=0 平面上的图与正交棋盘逐像素重合，交接无跳变。

export type PopupPhase = 'lift' | 'rise' | 'idle';
export type PopupLayer = {
  texture: THREE.Texture;
  /** 第 0 层（整张背板，无 alpha）为 null */
  alpha: AlphaGrid | null;
  /** 柔和投影用的模糊 alpha；第 0 层为 null */
  blur: THREE.Texture | null;
  /** 更近各层覆盖范围的并集（alpha）；更近的层还没出现时，本层在这里替补画原图。最近一层为 null */
  above: THREE.Texture | null;
  /** 原图（只挂在第 0 层；分层缺失、第 0 层就是原图时不设） */
  picture?: THREE.Texture;
};
export type TapResult = { kind: 'cat' | 'rune' | 'miss' | 'none'; layer: number | null; dist?: number; radius?: number };

/** 合缝回弹 + 暖光淡出仍在 BoardScene 上播完（mergePop 520ms），之后交接 */
export const HANDOVER_MS = 520;
const POP_MS = 600;
const SWEEP_MS = 900;
const GLOW_FADE_MS = 350;
const DOLLY_START = HANDOVER_MS + 780;
const DOLLY_MS = 700;
export const RISE_START = HANDOVER_MS + 1480;
const STAGGER_MS = 160;
const RISE_MS = 650;
/** 第 k 层最终高度 = k × LAYER_STEP × 棋盘宽 */
const LAYER_STEP = 0.11;
const FOV_FLAT = 0.5;
const FOV_BOOK = 26;
/** 面片比图大一圈，留给纸厚侧边画到图外 */
const PAD = 0.04;
/** 纸板厚度（CSS px）：只用于侧边透视，夸张一点才读得出 */
const THICK_PX = 7;
/** 倾斜/旋转的支点高度占最高层的比例（让层叠整体绕中部转，画面不甩出去） */
const PIVOT_FRAC = 0.4;
/** 立起后相机比精确 dolly 再退后一点，给 ±15° 倾斜留出屏幕边距 */
const PULL_BACK = 0.12;
/** 命中区域换算到屏幕上直径至少 44pt（半径 ≥ 22pt，spec §6） */
export const MIN_HIT_RADIUS_PX = 22;
/** 光从左上前方来（书本局部坐标） */
const LIGHT = new THREE.Vector3(-0.32, 0.45, 1).normalize();
const DEG = Math.PI / 180;

const FACE_VERT = /* glsl */ `
varying vec2 vUv;
varying vec3 vLocal;
void main() {
  vUv = uv;
  vLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const COMMON = /* glsl */ `
varying vec2 vUv;
varying vec3 vLocal;
float inside(vec2 uv) { return step(0.0, uv.x) * step(0.0, uv.y) * step(uv.x, 1.0) * step(uv.y, 1.0); }
`;

const FACE_FRAG = /* glsl */ `
uniform sampler2D map;
uniform float isBase;
uniform sampler2D aboveMap;
uniform sampler2D pictureMap;
uniform float aboveAmt;
uniform float opacity;
uniform float light;
uniform float sheenPos;
uniform float sheenAmt;
uniform float thick;
uniform float gradAmt;
uniform vec3 camLocal;
uniform vec3 edgeColor;
uniform vec3 gradeMul;
${COMMON}
float alphaAt(vec2 uv) {
  float i = inside(uv);
  return isBase > 0.5 ? i : i * texture2D(map, clamp(uv, 0.0, 1.0)).a;
}
void main() {
  vec4 tex = texture2D(map, clamp(vUv, 0.0, 1.0));
  float a = isBase > 0.5 ? inside(vUv) : inside(vUv) * tex.a;
  // 更近的层还没出现：在它们的覆盖范围里替补画原图（每层只含自己那段深度，平放时这样才等于原图）
  if (aboveAmt > 0.0) {
    float ab = aboveAmt * inside(vUv) * texture2D(aboveMap, clamp(vUv, 0.0, 1.0)).a;
    vec3 pic = texture2D(pictureMap, clamp(vUv, 0.0, 1.0)).rgb;
    float na = a + ab * (1.0 - a);
    tex.rgb = na > 0.0 ? mix(tex.rgb * a, pic, ab) / max(na, 1e-4) : tex.rgb;
    a = na;
  }
  // 柔和的左上→右下明暗 + 通关扫过的一道暖光
  float grad = 1.0 + gradAmt * ((vUv.y - 0.5) - (vUv.x - 0.5));
  // 每层只含自己那段深度，被更近内容挡住的部分在流水线里已补画（tools/layers.py），不再有"重影"
  vec3 base = tex.rgb;
  float d = (vUv.x + (1.0 - vUv.y)) * 0.5 - sheenPos;
  vec3 col = base * light * grad + sheenAmt * exp(-d * d / 0.003) * vec3(1.0, 0.86, 0.55);
  // 纸板厚度：沿视线方向看到的背面轮廓（背面在 -thick 处，投到本平面上平移 thick * v.xy / v.z）
  vec3 v = camLocal - vLocal;
  vec2 shift = thick * v.xy / max(v.z, 1e-3);
  float e = thick > 0.0 ? max(alphaAt(vUv - shift), alphaAt(vUv - shift * 0.5)) : 0.0;
  // 侧边朝光的一面亮一点
  float sideLit = 0.86 + 0.14 * clamp(dot(normalize(vec2(-shift.x, -shift.y) + 1e-5), vec2(-0.58, 0.81)), -1.0, 1.0);
  float outA = a + e * (1.0 - a);
  if (outA < 0.003) discard;
  vec3 rgb = (col * a + edgeColor * light * sideLit * e * (1.0 - a)) / outA;
  gl_FragColor = vec4(rgb * gradeMul, outA * opacity);
  #include <colorspace_fragment>
}`;

const SHADOW_FRAG = /* glsl */ `
uniform sampler2D casterMap;
uniform sampler2D casterBlur;
uniform sampler2D recvMap;
uniform float recvIsBase;
uniform float soft;
uniform float strength;
uniform vec2 off;
uniform vec3 shadowColor;
${COMMON}
void main() {
  vec2 cu = vUv - off;
  vec2 cc = clamp(cu, 0.0, 1.0);
  float s = mix(texture2D(casterMap, cc).a, texture2D(casterBlur, cc).a, soft) * inside(cu);
  float r = inside(vUv) * (recvIsBase > 0.5 ? 1.0 : texture2D(recvMap, clamp(vUv, 0.0, 1.0)).a);
  float a = s * r * strength;
  if (a < 0.003) discard;
  gl_FragColor = vec4(shadowColor, a);
  #include <colorspace_fragment>
}`;

const SWEEP_VERT = /* glsl */ `
attribute float perim;
attribute float across;
varying float vP;
varying float vA;
void main() {
  vP = perim;
  vA = across;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const SWEEP_FRAG = /* glsl */ `
uniform float head;
uniform float travelled;
uniform float glow;
uniform vec3 color;
varying float vP;
varying float vA;
float d1(float h) { return fract(h - vP + 1.0); }
float trail(float d) {
  float lit = step(d, travelled);
  return max(exp(-d * 9.0) * lit, 0.45 * lit);
}
void main() {
  float da = d1(head);
  float db = d1(head + 0.5);
  float t = max(trail(da), trail(db));
  // 光头处一点近白的高光，其余是暖金
  float core = max(exp(-da * 60.0) * step(da, travelled), exp(-db * 60.0) * step(db, travelled));
  float a = clamp(t * glow * (1.0 - vA * vA) * 1.15, 0.0, 1.0);
  if (a < 0.003) discard;
  gl_FragColor = vec4(mix(color, vec3(1.0, 0.96, 0.82), core * 0.8), a);
  #include <colorspace_fragment>
}`;

/** 平面：位置 ±(0.5+PAD)，uv 从 -PAD 到 1+PAD（图占中间 0..1） */
function paddedPlane(): THREE.PlaneGeometry {
  const g = new THREE.PlaneGeometry(1 + 2 * PAD, 1 + 2 * PAD);
  const uv = g.attributes.uv.array as Float32Array;
  for (let i = 0; i < uv.length; i++) uv[i] = uv[i] * (1 + 2 * PAD) - PAD;
  g.attributes.uv.needsUpdate = true;
  return g;
}

/** 沿图外沿的一圈带子：perim 0..1 顺时针（左上起），across -1..1 跨宽 */
function sweepGeometry(width: number): THREE.BufferGeometry {
  const h = 0.5;
  const w = width / 2;
  const corners: [number, number][] = [[-h, h], [h, h], [h, -h], [-h, -h]];
  const pos: number[] = [];
  const perim: number[] = [];
  const across: number[] = [];
  const index: number[] = [];
  for (let s = 0; s < 4; s++) {
    const [ax, ay] = corners[s];
    const [bx, by] = corners[(s + 1) % 4];
    const dx = Math.sign(bx - ax);
    const dy = Math.sign(by - ay);
    // 外法线（顺时针走，左手侧朝外）
    const nx = -dy;
    const ny = dx;
    const a0 = [ax - dx * w, ay - dy * w];
    const b0 = [bx + dx * w, by + dy * w];
    const base = pos.length / 3;
    for (const [px, py, p] of [[a0[0], a0[1], s / 4], [b0[0], b0[1], (s + 1) / 4]] as const) {
      pos.push(px - nx * w, py - ny * w, 0, px + nx * w, py + ny * w, 0);
      perim.push(p, p);
      across.push(-1, 1);
    }
    index.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('perim', new THREE.Float32BufferAttribute(perim, 1));
  g.setAttribute('across', new THREE.Float32BufferAttribute(across, 1));
  g.setIndex(index);
  return g;
}

type Face = {
  mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial>;
  shadow: THREE.Mesh<THREE.PlaneGeometry, THREE.ShaderMaterial> | null;
};

type Burst = {
  ring: THREE.Mesh<THREE.RingGeometry, THREE.MeshBasicMaterial>;
  points: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial> | null;
  dirs: THREE.Vector2[];
  t: number;
  ms: number;
  radius: number;
  layer: number;
};

export type PopupOptions = {
  /** 图在世界中的位置（w × h）；默认 = 棋盘位置 boardRect(vp, aspect)（与 BoardScene 交接） */
  rectFor?: (vp: Viewport) => BoardRect;
  /** 图的宽 / 高；默认 1（城市拼图是方的；塔罗竖版 2/3；地图看图） */
  aspect?: number;
  /** 第二个可找的东西（塔罗的符文）：与猫同样的判定（命中半径换算到屏幕至少 22pt） */
  rune?: Circle;
  /** 符文已经收过（回看立体书）：不再判定，点到只算普通点按 */
  runeFound?: boolean;
};

/** 发光精灵（终章地图上放在 hotspot.layer 平面上） */
export type PopupMarker = { x: number; y: number; layer: number; r: number; color?: number; strength?: number };

export class PopupScene {
  readonly scene = new THREE.Scene();
  readonly camera = new THREE.PerspectiveCamera(FOV_FLAT, 1, 1, 10);
  private readonly book = new THREE.Group();
  private readonly faces: Face[] = [];
  private readonly bg: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly dropShadow: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly sweep: THREE.Mesh<THREE.BufferGeometry, THREE.ShaderMaterial>;
  private readonly raycaster = new THREE.Raycaster();
  private readonly bursts: Burst[] = [];
  private vp: Viewport = { width: 1, height: 1 };
  private rect: BoardRect = { cx: 0, cy: 0, w: 1, h: 1 };
  private t = -1;
  private tilt = { x: 0, y: 0 };
  private readonly aspect: number;
  private readonly rectFor: (vp: Viewport) => BoardRect;
  private readonly markers: { spec: PopupMarker; sprite: THREE.Sprite }[] = [];
  private markerTime = 0;
  /** 昼夜调色（终章地图用；拼图立体书保持 1） */
  private readonly gradeMul = new THREE.Color(1, 1, 1);
  catFound = false;
  runeFound = false;
  private readonly rune: Circle | null;
  private readonly runeFoundInit: boolean;

  constructor(private readonly layers: PopupLayer[], private readonly cat: Circle, opts: PopupOptions = {}) {
    this.aspect = opts.aspect ?? 1;
    this.rune = opts.rune ? { ...opts.rune, layer: Math.min(opts.rune.layer, layers.length - 1) } : null;
    this.runeFoundInit = !!opts.runeFound;
    this.runeFound = this.runeFoundInit;
    const aspect = this.aspect;
    this.rectFor = opts.rectFor ?? ((vp) => boardRect(vp, aspect));
    const paper = createPaperTexture();
    this.bg = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.MeshBasicMaterial({ map: paper, depthTest: false, depthWrite: false }));
    this.bg.renderOrder = 0;
    this.dropShadow = new THREE.Mesh(
      new THREE.PlaneGeometry(1, 1),
      new THREE.MeshBasicMaterial({ map: createShadowTexture(), transparent: true, depthTest: false, depthWrite: false }),
    );
    this.dropShadow.renderOrder = 1;
    this.book.add(this.dropShadow);

    const geo = paddedPlane();
    const edgeColor = new THREE.Color(0xcdb894);
    layers.forEach((layer, k) => {
      const mesh = new THREE.Mesh(
        geo,
        new THREE.ShaderMaterial({
          vertexShader: FACE_VERT,
          fragmentShader: FACE_FRAG,
          transparent: true,
          depthTest: false,
          depthWrite: false,
          uniforms: {
            map: { value: layer.texture },
            isBase: { value: k === 0 ? 1 : 0 },
            aboveMap: { value: layer.above ?? layer.texture },
            pictureMap: { value: layers[0].picture ?? layer.texture },
            aboveAmt: { value: 0 },
            opacity: { value: 1 },
            light: { value: 1 },
            sheenPos: { value: -1 },
            sheenAmt: { value: 0 },
            thick: { value: 0 },
            gradAmt: { value: 0 },
            camLocal: { value: new THREE.Vector3(0, 0, 1) },
            edgeColor: { value: edgeColor },
            gradeMul: { value: this.gradeMul },
          },
        }),
      );
      mesh.renderOrder = 2 * k + 2;
      let shadow: Face['shadow'] = null;
      if (k > 0 && layer.blur) {
        shadow = new THREE.Mesh(
          geo,
          new THREE.ShaderMaterial({
            vertexShader: FACE_VERT,
            fragmentShader: SHADOW_FRAG,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            uniforms: {
              casterMap: { value: layer.texture },
              casterBlur: { value: layer.blur },
              recvMap: { value: layers[k - 1].texture },
              recvIsBase: { value: k === 1 ? 1 : 0 },
              soft: { value: 0 },
              strength: { value: 0 },
              off: { value: new THREE.Vector2() },
              shadowColor: { value: new THREE.Color(0x4a3016) },
            },
          }),
        );
        shadow.renderOrder = 2 * k + 1;
        this.book.add(shadow);
      }
      this.book.add(mesh);
      this.faces.push({ mesh, shadow });
    });

    this.sweep = new THREE.Mesh(
      sweepGeometry(0.03),
      new THREE.ShaderMaterial({
        vertexShader: SWEEP_VERT,
        fragmentShader: SWEEP_FRAG,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          head: { value: 0 },
          travelled: { value: 0 },
          glow: { value: 0 },
          color: { value: new THREE.Color(0xeaa52a) },
        },
      }),
    );
    this.sweep.renderOrder = 100;
    this.book.add(this.sweep);
    this.scene.add(this.bg, this.book);
    this.reset();
  }

  get layerCount(): number {
    return this.layers.length;
  }

  get started(): boolean {
    return this.t >= 0;
  }

  /** 交接前（合缝回弹还在播）画面应继续由 BoardScene 渲染 */
  get wantsBoard(): boolean {
    return this.t < HANDOVER_MS;
  }

  get phase(): PopupPhase {
    if (this.t < RISE_START) return 'lift';
    return this.t < this.riseEnd ? 'rise' : 'idle';
  }

  private get riseEnd(): number {
    const n = this.layers.length - 1;
    return n <= 0 ? RISE_START : RISE_START + (n - 1) * STAGGER_MS + RISE_MS;
  }

  /** 第 k 层起立进度（easeOutBack，可略超 1） */
  private riseK(k: number): number {
    if (k === 0) return 0;
    const p = (this.t - RISE_START - (k - 1) * STAGGER_MS) / RISE_MS;
    return p <= 0 ? 0 : p >= 1 ? 1 : easeOutBack(p);
  }

  /** 图高（世界 px） */
  private get H(): number {
    return this.rect.h;
  }

  private get layerStep(): number {
    return LAYER_STEP * this.rect.w;
  }

  setViewport(vp: Viewport): void {
    this.vp = vp;
    this.rect = this.rectFor(vp);
    this.camera.aspect = vp.width / vp.height;
    const sw = vp.width * 1.4;
    const sh = vp.height * 1.4;
    this.bg.scale.set(sw, sh, 1);
    this.bg.position.z = -30;
    // 纹理坐标与 BoardScene 的纸底（铺满视口、每 256px 重复一次）在 z≈0 处对齐
    const map = this.bg.material.map!;
    map.repeat.set(sw / 256, sh / 256);
    map.offset.set((vp.width - sw) / 2 / 256, (vp.height - sh) / 2 / 256);
    this.apply();
  }

  /** fromMs：0 = 从棋盘交接开始（通关）；HANDOVER_MS = 没有棋盘，直接从平放的图开始浮起（查看立体书 / 终章） */
  start(fromMs = 0): void {
    this.reset();
    this.t = fromMs;
    this.apply();
  }

  /** 直接跳到立好的状态（终章之后的地图） */
  showIdle(): void {
    this.reset();
    this.t = this.riseEnd + GLOW_FADE_MS + SWEEP_MS + 1;
    this.apply();
  }

  reset(): void {
    this.t = -1;
    this.catFound = false;
    this.runeFound = this.runeFoundInit;
    for (const b of [...this.bursts]) this.removeBurst(b);
  }

  update(dtMs: number, tilt: { x: number; y: number }): void {
    if (this.t < 0) return;
    this.t += dtMs;
    this.markerTime += dtMs;
    this.tilt = tilt;
    for (const b of [...this.bursts]) {
      b.t += dtMs;
      if (b.t >= b.ms) this.removeBurst(b);
    }
    this.apply();
  }

  /** 把当前时间 t 映射到全部几何/材质参数 */
  private apply(): void {
    const t = Math.max(0, this.t);
    const W = this.rect.w;
    const H = this.H;
    const vp = this.vp;

    // 相机：从近似正交（fov 0.5°）推到轻微透视，z=0 平面始终正好铺满视口高度（dolly zoom）
    const dk = easeInOut(clamp01((t - DOLLY_START) / DOLLY_MS));
    const fov = FOV_FLAT + (FOV_BOOK - FOV_FLAT) * dk;
    const dist = (vp.height / 2 / Math.tan((fov * DEG) / 2)) * (1 + PULL_BACK * dk);
    this.camera.fov = fov;
    this.camera.position.set(0, 0, dist);
    this.camera.near = Math.max(1, dist - W * 2);
    this.camera.far = dist + W * 2;
    this.camera.updateProjectionMatrix();

    // 浮起：整图放大回弹（交接后 POP_MS 内 1 → 1.05 → 1），投影随之加深外扩
    const lt = t - HANDOVER_MS;
    const pop = lt > 0 && lt < POP_MS ? 0.05 * Math.sin((Math.PI * lt) / POP_MS) : 0;
    const lifted = easeOutCubic(clamp01(lt / POP_MS));
    const top = (this.layers.length - 1) * this.layerStep;
    const pivot = top * PIVOT_FRAC;
    this.book.position.set(this.rect.cx, this.rect.cy, pivot);
    this.book.scale.setScalar(1 + pop);

    // 倾斜：起立过程中渐入
    const riseAll = clamp01((t - RISE_START) / Math.max(1, this.riseEnd - RISE_START));
    const tiltW = easeInOut(riseAll);
    this.book.rotation.set(this.tilt.x * DEG * tiltW, this.tilt.y * DEG * tiltW, 0);

    // 背板下的落影：与 BoardScene 的棋盘投影一致（+4,-8，1.5×），浮起后更远更淡
    this.dropShadow.position.set(4 + 8 * lifted, -8 - 12 * lifted, -pivot - 2);
    this.dropShadow.scale.set(W * (1.5 + 0.08 * lifted), H * (1.5 + 0.08 * lifted), 1);
    this.dropShadow.material.opacity = 1 - 0.15 * lifted;

    // 扫光：两道金光从左上、右下同时出发沿外沿各走半圈，走完整圈亮一下再淡出
    const sk = clamp01(lt / SWEEP_MS);
    const su = this.sweep.material.uniforms;
    su.head.value = 0.5 * easeInOut(sk);
    su.travelled.value = su.head.value;
    const fadeOut = clamp01((lt - SWEEP_MS) / GLOW_FADE_MS);
    su.glow.value = lt > 0 ? (1 - fadeOut) * (0.75 + 0.25 * Math.sin(Math.PI * sk)) : 0;
    this.sweep.visible = su.glow.value > 0.001;
    this.sweep.position.z = -pivot + 0.5;
    this.sweep.scale.set(W, H, 1);

    this.book.updateMatrixWorld(true);
    this.camera.updateMatrixWorld(true);
    const camWorld = this.camera.position;
    const lightK = this.tiltLight();
    const sheenPos = -0.2 + 1.4 * clamp01(lt / SWEEP_MS);
    const sheenAmt = lt > 0 && lt < SWEEP_MS ? 0.22 * Math.sin(Math.PI * clamp01(lt / SWEEP_MS)) : 0;

    const layer0Picture = !!this.layers[0].picture;
    for (let k = 0; k < this.faces.length; k++) {
      const { mesh, shadow } = this.faces[k];
      const rk = k === 0 ? this.riseK(1) : this.riseK(k);
      const z = k * this.layerStep * (k === 0 ? 0 : this.riseK(k));
      mesh.position.set(0, 0, z - pivot + k * 0.02);
      mesh.scale.set(W, H, 1);
      // 上层在交接前不画（只有背板 = 原图）；起立的最初一段淡入，白边随之出现
      const visible = k === 0 || t >= RISE_START + (k - 1) * STAGGER_MS;
      mesh.visible = visible;
      const u = mesh.material.uniforms;
      u.opacity.value = k === 0 ? 1 : clamp01((t - RISE_START - (k - 1) * STAGGER_MS) / 120);
      // 下一层（更近）完全显出之前，本层替补画它及更近各层的原图内容
      u.aboveAmt.value = layer0Picture && this.layers[k].above ? 1 - clamp01((t - RISE_START - k * STAGGER_MS) / 120) : 0;
      // 越近越亮一点（盒子深处偏暗），整体明暗随倾斜轻微变化；平放时严格为 1，保证交接无色差
      const depthLight = 0.9 + 0.1 * (k / Math.max(1, this.faces.length - 1));
      u.light.value = 1 + (depthLight * lightK - 1) * clamp01(rk);
      u.sheenPos.value = sheenPos;
      u.sheenAmt.value = sheenAmt;
      u.thick.value = (THICK_PX / W) * clamp01(rk);
      u.gradAmt.value = 0.05 * clamp01(rk);
      mesh.updateMatrixWorld(true);
      (u.camLocal.value as THREE.Vector3).copy(camWorld);
      mesh.worldToLocal(u.camLocal.value as THREE.Vector3);

      if (shadow) {
        const below = (k - 1) * this.layerStep * (k - 1 === 0 ? 0 : this.riseK(k - 1));
        const h = Math.max(0, z - below);
        shadow.visible = visible && h > 0.5;
        shadow.position.set(0, 0, below - pivot + k * 0.02 - 0.01);
        shadow.scale.set(W, H, 1);
        const su2 = shadow.material.uniforms;
        // 光从左上前方：影子落在右下，位移 ∝ 高度差
        (su2.off.value as THREE.Vector2).set((-LIGHT.x / LIGHT.z) * h / W, (-LIGHT.y / LIGHT.z) * h / H);
        su2.soft.value = clamp01(h / this.layerStep);
        su2.strength.value = 0.55 * clamp01(h / (this.layerStep * 0.4));
      }
    }

    for (const b of this.bursts) this.applyBurst(b);
    this.applyMarkers();
  }

  /** 昼夜调色：整体乘色（色温 × 亮度），背景纸同样调 */
  setGrade(tint: THREE.Color, bright: number): void {
    this.gradeMul.copy(tint).multiplyScalar(bright);
    this.bg.material.color.copy(this.gradeMul);
  }

  /** 发光精灵整体强度（夜里更亮） */
  setMarkerStrength(k: number): void {
    for (const m of this.markers) m.spec.strength = k;
  }

  // ── 发光精灵（终章地图） ──

  setMarkers(list: PopupMarker[], texture: THREE.Texture): void {
    for (const m of this.markers) {
      this.book.remove(m.sprite);
      m.sprite.material.dispose();
    }
    this.markers.length = 0;
    for (const spec of list) {
      const sprite = new THREE.Sprite(
        new THREE.SpriteMaterial({ map: texture, color: spec.color ?? 0xffc061, transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending }),
      );
      sprite.renderOrder = 150;
      this.book.add(sprite);
      this.markers.push({ spec, sprite });
    }
    this.applyMarkers();
  }

  private applyMarkers(): void {
    const W = this.rect.w;
    const H = this.H;
    // 发光在本层立起后才出现
    for (const { spec, sprite } of this.markers) {
      const face = this.faceAt(spec.layer);
      const up = spec.layer === 0 ? clamp01((this.t - RISE_START) / 300) : clamp01(this.riseK(spec.layer));
      const pulse = 1 + 0.08 * Math.sin(this.markerTime / 420 + spec.x * 9);
      const size = spec.r * W * 3.2 * pulse;
      sprite.position.set((spec.x - 0.5) * W, (0.5 - spec.y) * H, face.position.z + 2);
      sprite.scale.set(size, size, 1);
      sprite.material.opacity = (spec.strength ?? 1) * up;
      sprite.visible = up > 0.001;
    }
  }

  /** 屏幕点 → 第 k 层上的图片坐标（归一化，y 向下）；射线与该层平面无交点时为 null */
  pointOnLayer(sx: number, sy: number, k: number): { x: number; y: number } | null {
    const uv = this.rayToLayerUV(sx, sy, k);
    return uv ? { x: uv.x, y: 1 - uv.y } : null;
  }

  /** 第 k 层上图片坐标（y 向下）→ 屏幕 CSS px */
  screenOf(k: number, x: number, y: number): { x: number; y: number } {
    return this.layerToScreen(k, x, y);
  }

  /** 第 k 层上 (x, y) 附近每单位图片宽度对应多少屏幕 px */
  pxPerUnitAt(k: number, x: number, y: number): number {
    const a = this.layerToScreen(k, x, y);
    const b = this.layerToScreen(k, x + 0.01, y);
    return Math.hypot(b.x - a.x, b.y - a.y) / 0.01;
  }

  /** 释放 GPU 资源（离开页面时）；纹理归调用方所有时 keepTextures = true */
  dispose(keepTextures = false): void {
    for (const b of [...this.bursts]) this.removeBurst(b);
    for (const m of this.markers) m.sprite.material.dispose();
    const geos = new Set<THREE.BufferGeometry>();
    const mats = new Set<THREE.Material>();
    this.scene.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.geometry) geos.add(m.geometry);
      if (m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach((x) => mats.add(x));
    });
    geos.forEach((g) => g.dispose());
    mats.forEach((m) => m.dispose());
    this.bg.material.map?.dispose();
    this.dropShadow.material.map?.dispose();
    if (!keepTextures) for (const l of this.layers) l.texture.dispose();
    if (!keepTextures) this.layers[0].picture?.dispose();
    for (const l of this.layers) l.blur?.dispose();
    for (const l of this.layers) l.above?.dispose();
  }

  /** 倾斜时整体受光：法线转向光源略亮，背离略暗（±4% 左右） */
  private tiltLight(): number {
    const n = new THREE.Vector3(0, 0, 1).applyQuaternion(this.book.quaternion);
    return 1 + 0.35 * (n.dot(LIGHT) - LIGHT.z);
  }

  // ── 找猫 ──

  private faceAt(k: number): THREE.Mesh {
    return this.faces[Math.max(0, Math.min(this.faces.length - 1, k))].mesh;
  }

  /** 屏幕点（CSS px）射线 → 第 k 层平面上的图片 uv（原点左下） */
  private rayToLayerUV(sx: number, sy: number, k: number): THREE.Vector2 | null {
    const ndc = new THREE.Vector2((sx / this.vp.width) * 2 - 1, -((sy / this.vp.height) * 2 - 1));
    this.raycaster.setFromCamera(ndc, this.camera);
    const mesh = this.faceAt(k);
    mesh.updateMatrixWorld(true);
    const normal = new THREE.Vector3(0, 0, 1).transformDirection(mesh.matrixWorld);
    const point = new THREE.Vector3().setFromMatrixPosition(mesh.matrixWorld);
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, point);
    const hit = this.raycaster.ray.intersectPlane(plane, new THREE.Vector3());
    if (!hit) return null;
    const local = mesh.worldToLocal(hit);
    return new THREE.Vector2(local.x + 0.5, local.y + 0.5);
  }

  /** 第 k 层上图片坐标（原图归一化，y 向下）→ 屏幕 CSS px */
  private layerToScreen(k: number, x: number, y: number): { x: number; y: number } {
    const mesh = this.faceAt(k);
    mesh.updateMatrixWorld(true);
    const p = new THREE.Vector3(x - 0.5, 0.5 - y, 0).applyMatrix4(mesh.matrixWorld).project(this.camera);
    return { x: ((p.x + 1) / 2) * this.vp.width, y: ((1 - p.y) / 2) * this.vp.height };
  }

  catScreen(): { x: number; y: number } {
    return this.layerToScreen(this.cat.layer, this.cat.x, this.cat.y);
  }

  /** 符文在当前画面上的屏幕坐标（测试 / 飞行动画起点）；没有符文 = null */
  runeScreen(): { x: number; y: number } | null {
    return this.rune ? this.layerToScreen(this.rune.layer, this.rune.x, this.rune.y) : null;
  }

  /** 屏幕上每单位图片宽度对应多少 CSS px（在圆所在层、圆心附近量） */
  private pxPerUnit(c: Circle = this.cat): number {
    const a = this.layerToScreen(c.layer, c.x, c.y);
    const b = this.layerToScreen(c.layer, c.x + 0.01, c.y);
    return Math.hypot(b.x - a.x, b.y - a.y) / 0.01;
  }

  /** 点按到圆心的距离（图宽单位）与命中半径；射线不交该层 = null */
  private probe(sx: number, sy: number, c: Circle): { dist: number; radius: number } | null {
    const uv = this.rayToLayerUV(sx, sy, c.layer);
    if (!uv) return null;
    // x、r 按图宽归一，y 按图高归一：竖向差换算成"图宽"单位再量（竖版图 aspect = 2/3）
    const dist = Math.hypot(uv.x - c.x, (1 - uv.y - c.y) / this.aspect);
    return { dist, radius: Math.max(c.r, MIN_HIT_RADIUS_PX / this.pxPerUnit(c)) };
  }

  /** 点按（已按 8px 阈值区分过拖动）。只在 idle（结算条出现后）判定；猫和符文都中时取更近的 */
  tap(sx: number, sy: number): TapResult {
    if (this.phase !== 'idle') return { kind: 'none', layer: null };
    const top = this.topLayerAt(sx, sy);
    const cat = this.catFound ? null : this.probe(sx, sy, this.cat);
    const rune = this.rune && !this.runeFound ? this.probe(sx, sy, this.rune) : null;
    const catHit = cat && cat.dist <= cat.radius ? cat.dist / cat.radius : Infinity;
    const runeHit = rune && rune.dist <= rune.radius ? rune.dist / rune.radius : Infinity;
    if (catHit < Infinity && catHit <= runeHit) {
      this.catFound = true;
      this.addBurst(this.cat.layer, this.cat.x, this.cat.y, true);
      return { kind: 'cat', layer: this.cat.layer, dist: cat!.dist, radius: cat!.radius };
    }
    if (runeHit < Infinity && this.rune) {
      this.runeFound = true;
      this.addBurst(this.rune.layer, this.rune.x, this.rune.y, true, true);
      return { kind: 'rune', layer: this.rune.layer, dist: rune!.dist, radius: rune!.radius };
    }
    if (top) this.addBurst(top.layer, top.x, top.y, false);
    return { kind: 'miss', layer: top?.layer ?? null, ...(cat ? { dist: cat.dist, radius: cat.radius } : {}) };
  }

  /** 手指下最上面那层纸（按低分辨率 alpha 判定）及其图片坐标（y 向下） */
  private topLayerAt(sx: number, sy: number): { layer: number; x: number; y: number } | null {
    for (let k = this.layers.length - 1; k >= 0; k--) {
      const uv = this.rayToLayerUV(sx, sy, k);
      if (!uv) continue;
      const grid = this.layers[k].alpha;
      const a = grid ? alphaAt(grid, uv.x, uv.y) : uv.x >= 0 && uv.x <= 1 && uv.y >= 0 && uv.y <= 1 ? 1 : 0;
      if (a > 0.5) return { layer: k, x: uv.x, y: 1 - uv.y };
    }
    return null;
  }

  // ── 点按反馈：命中 = 金圈 + 纸屑；未命中 = 一圈很淡的纸面涟漪 ──

  /** gold = 符文：只有金色的光点，光圈更大更亮 */
  private addBurst(layer: number, x: number, y: number, hit: boolean, gold = false): void {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.78, 1, 40),
      new THREE.MeshBasicMaterial({
        color: hit ? 0xf6c25b : 0x6b4423,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        blending: hit ? THREE.AdditiveBlending : THREE.NormalBlending,
      }),
    );
    ring.renderOrder = 200;
    ring.userData.x = x;
    ring.userData.y = y;
    let points: Burst['points'] = null;
    const dirs: THREE.Vector2[] = [];
    if (hit) {
      const n = 14;
      const pos = new Float32Array(n * 3);
      const col = new Float32Array(n * 3);
      const palette = (gold ? [0xf6c25b, 0xffe9a8, 0xfff6d8, 0xe0a84a] : [0xf6c25b, 0xe2574c, 0x6fa8dc, 0xfffaf0, 0x8bc34a]).map((c) => new THREE.Color(c));
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2 + (i % 3) * 0.3;
        dirs.push(new THREE.Vector2(Math.cos(a), Math.sin(a)).multiplyScalar(0.7 + 0.3 * ((i * 7) % 5) / 4));
        palette[i % palette.length].toArray(col, i * 3);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
      points = new THREE.Points(
        g,
        new THREE.PointsMaterial({ size: 7, sizeAttenuation: false, vertexColors: true, transparent: true, depthTest: false, depthWrite: false }),
      );
      points.renderOrder = 201;
      points.frustumCulled = false;
      this.book.add(points);
    }
    this.book.add(ring);
    const b: Burst = { ring, points, dirs, t: 0, ms: hit ? (gold ? 1200 : 900) : 420, radius: hit ? (gold ? 62 : 46) : 16, layer };
    this.bursts.push(b);
    this.applyBurst(b);
  }

  private applyBurst(b: Burst): void {
    const face = this.faceAt(b.layer);
    const W = this.rect.w;
    const k = clamp01(b.t / b.ms);
    const e = easeOutCubic(k);
    const x = (b.ring.userData.x - 0.5) * W;
    const y = (0.5 - b.ring.userData.y) * this.H;
    const z = face.position.z + 1;
    b.ring.position.set(x, y, z);
    b.ring.scale.setScalar(Math.max(0.01, b.radius * (0.25 + 0.75 * e)));
    b.ring.material.opacity = (b.points ? 0.95 : 0.35) * (1 - k);
    if (b.points) {
      const pos = b.points.geometry.attributes.position.array as Float32Array;
      b.dirs.forEach((d, i) => {
        const r = b.radius * 1.5 * e;
        pos[i * 3] = x + d.x * r;
        pos[i * 3 + 1] = y + d.y * r - 30 * k * k; // 纸屑落下
        pos[i * 3 + 2] = z + 2;
      });
      b.points.geometry.attributes.position.needsUpdate = true;
      b.points.material.opacity = 1 - k * k;
    }
  }

  private removeBurst(b: Burst): void {
    this.book.remove(b.ring);
    b.ring.geometry.dispose();
    b.ring.material.dispose();
    if (b.points) {
      this.book.remove(b.points);
      b.points.geometry.dispose();
      b.points.material.dispose();
    }
    this.bursts.splice(this.bursts.indexOf(b), 1);
  }

  /** 画面上纸层实际覆盖的屏幕范围（按 alpha 采样，测试用来验证结算条不挡图） */
  pictureScreenBounds(): { left: number; top: number; right: number; bottom: number } {
    const box = { left: Infinity, top: Infinity, right: -Infinity, bottom: -Infinity };
    const S = 32;
    this.faces.forEach((f, k) => {
      if (!f.mesh.visible) return;
      const grid = this.layers[k].alpha;
      for (let j = 0; j <= S; j++) {
        for (let i = 0; i <= S; i++) {
          const x = Math.min(i / S, 0.999);
          const y = Math.min(j / S, 0.999);
          if (grid && alphaAt(grid, x, 1 - y) <= 0.5) continue;
          const p = this.layerToScreen(k, x, y);
          box.left = Math.min(box.left, p.x);
          box.right = Math.max(box.right, p.x);
          box.top = Math.min(box.top, p.y);
          box.bottom = Math.max(box.bottom, p.y);
        }
      }
    });
    return box;
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}
